import { YesNoAnswer } from 'scenes/common/types';
import {
  Driver,
  DriverGender,
  MotorFlowDefinition,
  MotorFlowType,
  MotorFormAnswer,
  MotorFunnelState,
  MotorStepType,
  OptionalExtra,
  PolicyHolder,
} from 'scenes/motor/types';

import {
  getSelectedExtras,
  isComprehensiveSelected,
  isUnder25OptionalExtraSelected,
} from 'scenes/motor/selectors';
import { skipPromoPage } from 'scenes/motor/helpers/campaign';
import { toWireMotorParties } from 'scenes/motor/api/quote/marshaller/party';
import { WireMotorParty } from 'scenes/motor/api/quote/types';

export const toPolicyHolder = (
  policyHolder: Partial<Driver>,
  isSelected: boolean = false
): PolicyHolder => ({
  firstName: policyHolder.firstName ?? '',
  lastName: policyHolder.lastName ?? '',
  gender: policyHolder.gender ?? DriverGender.Unspecified,
  dob: policyHolder.dob ?? '',
  isSelected,
});

export const forwardLinks: MotorFlowDefinition = {
  [MotorStepType.QuotePrice]: (funnelState: MotorFunnelState) => ({
    step: MotorStepType.Excess,
    policyDetails: {
      ...funnelState.policyDetails,
      preselectedPaymentFrequency:
        funnelState.coverOptions.preselectedPaymentFrequency,
    },
  }),
  [MotorStepType.Excess]: (funnelState: MotorFunnelState) => ({
    step: isComprehensiveSelected(funnelState)
      ? MotorStepType.MarketAgreedValue
      : MotorStepType.OptionalExtras,
  }),
  [MotorStepType.MarketAgreedValue]: { step: MotorStepType.OptionalExtras },
  [MotorStepType.OptionalExtras]: (funnelState: MotorFunnelState) => {
    const parties: WireMotorParty[] = toWireMotorParties(funnelState);
    const extras = [...getSelectedExtras(funnelState)];
    const isUnder25ExtraSelected = extras.indexOf(OptionalExtra.Under25) > -1;
    const apiResponsesQuoteDetails = {
      ...funnelState.apiResponses.quoteDetails,
    };
    if (apiResponsesQuoteDetails.data) {
      apiResponsesQuoteDetails.data.payload.parties = [...parties];
    }

    return {
      step: MotorStepType.addDriverName,
      flow: MotorFlowType.PolicyDetails,
      apiResponses: {
        ...funnelState.apiResponses,
        quoteDetails: {
          ...funnelState.apiResponses.quoteDetails,
          ...apiResponsesQuoteDetails,
        },
      },
      quoteDetails: {
        ...funnelState.quoteDetails,
        coverForDriversUnder25:
          (isUnder25ExtraSelected && YesNoAnswer.Yes) || YesNoAnswer.No,
      },
    };
  },
};

export const backwardLinks: MotorFlowDefinition = {
  [MotorStepType.QuotePrice]: (state: MotorFunnelState) => ({
    step: skipPromoPage ? MotorStepType.FlybuysNumber : MotorStepType.PromoCode,
    flow: MotorFlowType.QuoteDetails,
    quoteDetails: {
      ...state.quoteDetails,
      coverForDriversUnder25: isUnder25OptionalExtraSelected(state)
        ? YesNoAnswer.Yes
        : YesNoAnswer.No,
    },
  }),
  [MotorStepType.Excess]: { step: MotorStepType.QuotePrice },
  [MotorStepType.MarketAgreedValue]: { step: MotorStepType.Excess },
  [MotorStepType.OptionalExtras]: (funnelState: MotorFunnelState) => ({
    step: isComprehensiveSelected(funnelState)
      ? MotorStepType.MarketAgreedValue
      : MotorStepType.Excess,
  }),
};

export const editCoverOptions = (
  funnelState: MotorFunnelState,
  action: MotorFormAnswer
) => ({
  step: action.targetStep,
  flow: MotorFlowType.CoverOptions,
});
